import { VENDOR_AGGREGATION_GROUP } from "@dransay/api/models";
import type { DeliveryMethodKind } from "@dransay/db";
import type { TFunction } from "i18next";
import {
	capPharmaciesByRadiusKm,
	filterIndividualPickerPharmacies,
	type VendorOptionForPicker,
} from "~/features/products/pharmacy-picker/map-vendor-to-picker-item";
import type { PharmacyPickerGroupShortcut } from "~/features/products/pharmacy-picker/pharmacy-picker.types";

// Pickup/courier are map-based and only surface pharmacies within this radius.
export const MAP_VENDOR_RADIUS_KM = 10;

type AggregatedVendorOption = {
	id: string;
	name?: string | null;
	count?: number | null;
};

/**
 * Builds the query string for the legacy `/api/vendors` list endpoint.
 *
 * Kept out of `VendorFilter` so the component body stays under the cognitive
 * complexity gate; the branching here is inherent to the endpoint's contract.
 */
export function buildVendorsQueryParams({
	search,
	postalCode,
	deliveryMethodKind,
	usePharmacyPicker,
	listIncludeNonTop,
	value,
	isAggregationGroup,
}: {
	search: string;
	postalCode: string | null;
	deliveryMethodKind?: DeliveryMethodKind;
	usePharmacyPicker: boolean;
	listIncludeNonTop: boolean;
	value?: string;
	isAggregationGroup: (vendorId: string) => boolean;
}): URLSearchParams {
	const params = new URLSearchParams();
	if (search) params.append("search", search);
	if (postalCode) params.append("postalCode", postalCode);
	if (deliveryMethodKind) {
		params.append("deliveryMethodKind", deliveryMethodKind);
	}
	if (usePharmacyPicker) {
		if (!search) {
			params.append(
				"aggregationGroup",
				listIncludeNonTop
					? VENDOR_AGGREGATION_GROUP.ALL
					: VENDOR_AGGREGATION_GROUP.TOP,
			);
		}
	} else if (!search && value && isAggregationGroup(value)) {
		params.append("aggregationGroup", value);
	}
	return params;
}

/**
 * Turns the aggregated Top/All options into the picker's group shortcuts,
 * dropping whichever of the two the current delivery method does not offer.
 */
export function buildPickerGroupShortcuts(
	additionalOptions: AggregatedVendorOption[] | undefined,
	t: TFunction<["product", "common"]>,
	/**
	 * Courier and Abholung offer one "all pharmacies" row and nothing else, so
	 * the Top shortcut is left out for them. Everything downstream keys off the
	 * shortcut's absence -- the Top chip, the why-top banner and the
	 * show-non-top recovery all disappear with it.
	 */
	includeTopGroup = true,
): PharmacyPickerGroupShortcut[] {
	const shortcuts: PharmacyPickerGroupShortcut[] = [];
	const topOption = includeTopGroup
		? additionalOptions?.find(
				(option) => option.id === VENDOR_AGGREGATION_GROUP.TOP,
			)
		: undefined;
	if (topOption) {
		shortcuts.push({
			id: VENDOR_AGGREGATION_GROUP.TOP,
			label: topOption.name ?? t("vendor.group.topShipping"),
			count: topOption.count ?? 0,
		});
	}
	const allOption = additionalOptions?.find(
		(option) => option.id === VENDOR_AGGREGATION_GROUP.ALL,
	);
	if (allOption) {
		shortcuts.push({
			id: VENDOR_AGGREGATION_GROUP.ALL,
			label: allOption.name ?? t("vendor.group.allShipping"),
			count: allOption.count ?? 0,
		});
	}
	return shortcuts;
}

/**
 * The picker shows its skeleton until a fetcher that has never resolved
 * settles; a fetcher that already holds data revalidates in the background.
 */
export function isVendorListLoading(
	listState: "idle" | "loading" | "submitting",
	hasListData: boolean,
	selectedState: "idle" | "loading" | "submitting",
	hasSelectedData: boolean,
): boolean {
	const listPending = listState !== "idle" && !hasListData;
	const selectedPending = selectedState === "loading" && !hasSelectedData;
	return listPending || selectedPending;
}

/**
 * `undefined` means no radius control is driving this picker, so keep the
 * legacy cap; `null` is an explicit "any radius" and skips capping entirely.
 */
export function resolveListRadiusKm(
	listRadiusKm: number | null | undefined,
): number | null {
	return listRadiusKm === undefined ? MAP_VENDOR_RADIUS_KM : listRadiusKm;
}

const matchesPharmacySearch = (
	pharmacy: { name: string; address?: string },
	query: string,
): boolean => {
	const normalizedQuery = query.trim().toLowerCase();
	if (!normalizedQuery) return true;
	return (
		pharmacy.name.toLowerCase().includes(normalizedQuery) ||
		(pharmacy.address?.toLowerCase().includes(normalizedQuery) ?? false)
	);
};

/**
 * Narrows the loaded vendors down to the individual pharmacies the picker
 * lists: inside the active radius when the delivery method is map-based, and
 * matching the client-side search when the filtered endpoint drives the list.
 */
export function buildPickerPharmacies({
	vendorOptions,
	isMapDelivery,
	listRadiusKm,
	enablePharmacyFilters,
	search,
}: {
	vendorOptions: VendorOptionForPicker[];
	isMapDelivery: boolean;
	listRadiusKm: number | null;
	enablePharmacyFilters: boolean;
	search: string;
}) {
	const all = filterIndividualPickerPharmacies(vendorOptions);
	const inRadius =
		isMapDelivery && listRadiusKm !== null
			? capPharmaciesByRadiusKm(all, listRadiusKm)
			: all;
	if (!enablePharmacyFilters) return inRadius;
	return inRadius.filter((pharmacy) => matchesPharmacySearch(pharmacy, search));
}

/**
 * Label shown on the closed picker trigger: the selected pharmacy's name, or
 * the aggregated group's name when a Top/All shortcut is what is selected.
 */
export function resolvePickerPlaceholder({
	value,
	vendorOptions,
	additionalOptions,
	isAggregationGroup,
}: {
	value?: string;
	vendorOptions: { value: string; title: string }[];
	additionalOptions: AggregatedVendorOption[] | undefined;
	isAggregationGroup: (vendorId: string) => boolean;
}): string | null | undefined {
	if (!value || !isAggregationGroup(value)) {
		return vendorOptions.find((option) => option.value === value)?.title;
	}
	return additionalOptions?.find((option) => option.id === value)?.name;
}
